import { useState, type ChangeEvent } from 'react';
import styled from 'styled-components';
import { useImportMeetingParticipantsMutation } from '@shared/api';

/**
 * Добавление участников совещания списком: CSV-выгрузка приглашённых из календаря
 * VK WorkSpace («участник (ФИО);email;статус участия во встрече») или строки,
 * вставленные вручную. Список разбирается здесь, пользователь отмечает, кого
 * добавить (по умолчанию все, кроме «Не идёт»), сервер сопоставляет со
 * справочником сотрудников по email и ФИО и не дублирует уже добавленных.
 */

export interface ParsedParticipant {
  name: string;
  email: string;
  status: string;
  selected: boolean;
}

/** Разбить строку CSV с учётом кавычек ("…" и экранирования ""). */
function splitCsvLine(line: string, delim: string): string[] {
  const out: string[] = [];
  let cur = '';
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else if (ch === '"') {
        quoted = false;
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === delim) {
      out.push(cur);
      cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out.map((c) => c.replace(/\s+/g, ' ').trim());
}

const EMAIL_RE = /[^\s;,<>"]+@[^\s;,<>"]+/;
const HEADER_RE = /участ|фио|email|e-mail|почта|имя|статус/i;
const DECLINED_RE = /не\s*ид[её]т|отказ/i;

/** Разобрать список участников: CSV (`;`, табуляция или `,`) или строки «ФИО email». */
export function parseParticipantsList(text: string): ParsedParticipant[] {
  const lines = text
    .replace(/^﻿/, '')
    .split(/\r?\n/)
    .filter((l) => l.trim() !== '');
  if (lines.length === 0) return [];
  const first = lines[0];
  const delim = [';', '\t', ','].find((d) => first.includes(d)) ?? null;
  const rows: ParsedParticipant[] = [];
  lines.forEach((line, idx) => {
    const cells = delim ? splitCsvLine(line, delim) : [line.trim()];
    if (idx === 0 && !EMAIL_RE.test(line) && cells.some((c) => HEADER_RE.test(c))) return;
    let email = '';
    const rest: string[] = [];
    for (const c of cells) {
      const m = !email ? c.match(EMAIL_RE) : null;
      if (m) {
        email = m[0];
        const leftover = c.replace(m[0], '').replace(/[<>()]/g, '').trim();
        if (leftover) rest.push(leftover);
      } else if (c) {
        rest.push(c);
      }
    }
    const name = rest[0] ?? '';
    const status = rest.slice(1).join(' ');
    if (!name && !email) return;
    rows.push({ name, email, status, selected: !DECLINED_RE.test(status) });
  });
  return rows;
}

/** Файл из календаря бывает в UTF-8 (с BOM) или в Windows-1251 (Excel). */
async function readListFile(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(buf);
  } catch {
    return new TextDecoder('windows-1251').decode(buf);
  }
}

const Panel = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(3)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Hint = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Paste = styled.textarea`
  font-size: 13px;
  font-family: inherit;
  min-height: 96px;
  padding: ${({ theme }) => theme.spacing(2)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  resize: vertical;
`;

const Row = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  align-items: center;
  flex-wrap: wrap;
`;

const Btn = styled.button<{ $variant?: 'primary' | 'ghost' }>`
  font-size: 13px;
  font-weight: 600;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(4)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  cursor: pointer;
  border: 1px solid
    ${({ theme, $variant }) => ($variant === 'ghost' ? theme.colors.border : theme.colors.primary)};
  background: ${({ theme, $variant }) => ($variant === 'ghost' ? 'transparent' : theme.colors.primary)};
  color: ${({ theme, $variant }) => ($variant === 'ghost' ? theme.colors.text.secondary : '#fff')};

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const FileLabel = styled.label`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};

  input {
    margin-left: ${({ theme }) => theme.spacing(2)};
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    text-align: left;
    padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }

  th {
    font-size: 12px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text.muted};
  }
`;

const Declined = styled.span`
  color: ${({ theme }) => theme.colors.text.muted};
`;

export function ParticipantsImport({ meetingId }: { meetingId: string }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [rows, setRows] = useState<ParsedParticipant[]>([]);
  const [info, setInfo] = useState<string | null>(null);
  const [importParticipants, { isLoading }] = useImportMeetingParticipantsMutation();

  function parse(source: string) {
    const parsed = parseParticipantsList(source);
    setRows(parsed);
    setInfo(parsed.length === 0 ? 'В списке не нашлось ни одного участника.' : null);
  }

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const content = await readListFile(file);
    setText(content);
    parse(content);
  }

  function toggle(i: number) {
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, selected: !r.selected } : r)));
  }

  async function submit() {
    const chosen = rows.filter((r) => r.selected);
    if (chosen.length === 0) return;
    try {
      const res = await importParticipants({
        meetingId,
        rows: chosen.map((r) => ({ name: r.name, email: r.email || undefined })),
      }).unwrap();
      const parts = [`Добавлено: ${res.added}`];
      if (res.already > 0) parts.push(`уже были в списке: ${res.already}`);
      let msg = parts.join(', ') + '.';
      if (res.not_in_directory.length > 0) {
        msg += ` Нет в справочнике сотрудников (добавлены с адресом из списка): ${res.not_in_directory.join(', ')}.`;
      }
      setInfo(msg);
      setRows([]);
      setText('');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('importMeetingParticipants failed', err);
      setInfo('Не удалось добавить участников.');
    }
  }

  if (!open) {
    return (
      <Row style={{ marginTop: 12 }}>
        <Btn $variant="ghost" type="button" onClick={() => setOpen(true)}>
          Добавить списком
        </Btn>
        {info && <Hint>{info}</Hint>}
      </Row>
    );
  }

  const selected = rows.filter((r) => r.selected).length;

  return (
    <Panel>
      <Hint>
        Загрузите выгрузку приглашённых из календаря VK WorkSpace (CSV: «участник (ФИО);email;статус»)
        или вставьте список — по строке на человека, ФИО и email. Участники сопоставляются со
        справочником сотрудников по email и ФИО; уже добавленные не дублируются.
      </Hint>
      <FileLabel>
        Файл CSV:
        <input type="file" accept=".csv,.txt,text/csv,text/plain" onChange={(e) => void onFile(e)} />
      </FileLabel>
      <Paste
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={'Никитин Сергей;nikitinsv@ip2016.ru;Идёт\nПушкина Зоя;pushkinazn@ip2016.ru;Решает'}
        aria-label="Список участников"
      />
      <Row>
        <Btn $variant="ghost" type="button" onClick={() => parse(text)} disabled={!text.trim()}>
          Разобрать
        </Btn>
        <Btn
          $variant="ghost"
          type="button"
          onClick={() => {
            setOpen(false);
            setRows([]);
            setText('');
            setInfo(null);
          }}
        >
          Закрыть
        </Btn>
      </Row>
      {rows.length > 0 && (
        <>
          <Table>
            <thead>
              <tr>
                <th aria-label="Добавить" />
                <th>ФИО</th>
                <th>Email</th>
                <th>Статус</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.email}-${r.name}-${i}`}>
                  <td>
                    <input
                      type="checkbox"
                      checked={r.selected}
                      onChange={() => toggle(i)}
                      aria-label={`Добавить ${r.name || r.email}`}
                    />
                  </td>
                  <td>{r.name || '—'}</td>
                  <td>{r.email || '—'}</td>
                  <td>{r.selected ? r.status || '—' : <Declined>{r.status || '—'}</Declined>}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Row>
            <Btn type="button" onClick={() => void submit()} disabled={isLoading || selected === 0}>
              {isLoading ? 'Добавляю…' : `Добавить отмеченных (${selected})`}
            </Btn>
            <Hint>«Не идёт» по умолчанию не отмечены — поставьте галочку, если нужно.</Hint>
          </Row>
        </>
      )}
      {info && <Hint>{info}</Hint>}
    </Panel>
  );
}
